/**
 * Vibration cues — the primary alert on every device (host and followers).
 *   30 s left     → one short buzz
 *   period change → a long double buzz
 *
 * Native (Capacitor) uses the Haptics plugin, which works on iOS where
 * `navigator.vibrate` does not exist. With the screen locked the JS timer
 * may be suspended, so `scheduleCueNotifications` pre-books local
 * notifications (with vibration) for every period boundary; the OS fires
 * them even if the app is asleep. Sound stays an optional, host-only extra.
 */
import { Capacitor } from '@capacitor/core';
import type { CuePeriod, PlanData } from '@coach/core';
import { cueNotes } from '@coach/core';

const NATIVE = Capacitor.isNativePlatform();

async function nativeVibrate(pattern: number[]): Promise<boolean> {
  if (!NATIVE) return false;
  try {
    const { Haptics } = await import('@capacitor/haptics');
    for (let i = 0; i < pattern.length; i++) {
      if (i % 2 === 0) await Haptics.vibrate({ duration: pattern[i]! });
      else await new Promise((r) => setTimeout(r, pattern[i]!));
    }
    return true;
  } catch {
    return false;
  }
}

function webVibrate(pattern: number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* unsupported */
  }
}

export async function buzzShort(): Promise<void> {
  if (!(await nativeVibrate([180]))) webVibrate([180]);
}
export async function buzzLong(): Promise<void> {
  if (!(await nativeVibrate([600, 150, 600]))) webVibrate([600, 150, 600]);
}
export async function buzzTiny(): Promise<void> {
  if (!(await nativeVibrate([40]))) webVibrate([40]);
}

// ─── Background notifications (native only) ────────────────────────────────
const ID_BASE = 4200;
// Paths inside the app bundle (Capacitor copies public/ to App/public/; the
// files are 16-bit PCM WAV, well under iOS's 30 s limit for notification sounds).
// Android's plugin resolves the same paths from the web assets.
const PERIOD_SOUND = 'public/sounds/period.wav';
const WARN_SOUND = 'public/sounds/warning.wav';

/**
 * Book a notification at each period start and 30 s before it, from
 * `anchorMs` (period-0 wall clock). Wording comes from core's `cueNotes`
 * (current period's end time, every group's activity, what's next).
 * Returns silently on web or without permission.
 */
export async function scheduleCueNotifications(plan: PlanData, anchorMs: number, describe: (i: number) => CuePeriod): Promise<void> {
  if (!NATIVE) return;
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    const perm = await LocalNotifications.requestPermissions();
    if (perm.display !== 'granted') return;
    await cancelCueNotifications();
    const now = Date.now();
    // A notification with no sound is delivered silently: on the lock screen
    // that means no beep AND no vibration. Giving each one the app's own beep
    // makes iOS ring it (silent switch off) or buzz it (silent switch on, or
    // in a pocket). `foreground: false` keeps them quiet while the app is open,
    // where the timer already buzzes/beeps itself — otherwise every cue doubles.
    const list = cueNotes(plan, anchorMs, describe)
      .filter((n) => n.atMs > now)
      .map((n) => ({
        id: ID_BASE + n.slot,
        title: n.title,
        body: n.body,
        schedule: { at: new Date(n.atMs), allowWhileIdle: true },
        sound: n.slot % 2 === 0 ? WARN_SOUND : PERIOD_SOUND,
        foreground: false,
      }));
    if (list.length) await LocalNotifications.schedule({ notifications: list });
  } catch (e) {
    console.warn('[haptics] could not schedule notifications', e);
  }
}

export async function cancelCueNotifications(): Promise<void> {
  if (!NATIVE) return;
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    const pending = await LocalNotifications.getPending();
    const ours = pending.notifications.filter((n) => n.id >= ID_BASE && n.id < ID_BASE + 1000);
    if (ours.length) await LocalNotifications.cancel({ notifications: ours.map((n) => ({ id: n.id })) });
  } catch {
    /* ignore */
  }
}
